import { useCallback, useMemo, useRef } from "react";
import { extractAirportIata, isSameFlightNumber } from "@/helpers/bookingHelpers";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  setPickupDatetime,
  setPickupFlightDelayMinutes,
  setPickupFlightLandingUsed,
  swapPickupForTerminal,
} from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectEditOriginal,
  selectPickup,
  selectPickupDatetime,
  selectPickupFlightChangedSinceCommit,
  selectPickupFlightDelayMinutes,
  selectPickupFlightDerivedArrival,
  selectPickupFlightFrozenReason,
  selectPickupFlightLandingUsed,
  selectPickupFlightNumber,
  selectViewOnly,
} from "@/redux/slices/clientBooking/selectors";
import {
  flightDateFromPickup,
  flightLandingUsedIso,
  flightPickupCommitValue,
  flightWillArrive,
  isFlightPickupFrozen,
  landingTime,
  pickupFromFlight,
  utcIsoToAirportIso,
} from "../flightPickup";
import { validatePickup } from "../pickupTime";
import { useFlightDetails } from "./useFlightDetails";
import { useTerminalSwap } from "./useTerminalSwap";

/**
 * The pickup, the wait and the landing as ONE claim. The server verifies them together —
 * `landing + delay*60 === pickup`, on exact epoch equality — so they are built from a single landing in a
 * single call and committed together. Reading the landing again at the call site is how a pickup ends up
 * paired with an arrival it was not derived from, which is precisely the pair the server refuses.
 */
export interface RederivedFlightPickup {
  /** The value to commit to `clientBooking.pickupDatetime`. */
  value: string;
  /** The booked wait, carried through so the payload's `flight_landing_used` stays verifiable. */
  delayMinutes: number;
  /** `flight_landing_used` — the landing this pickup was built on. Null when the landing is unparseable. */
  landingUsed: string | null;
}

/**
 * Resolves the booking's flight for as long as the SHEET is open, and re-derives the pickup from the
 * latest landing when the save happens.
 *
 * ## The rule this exists for
 *
 * If someone opens a booking and saves it, the pickup is built from the latest flight data — whatever they
 * came in to change. Editing the drop-off, the vehicle type or the notes is not editing the flight, and
 * none of them should have to be the date field before the flight is re-checked.
 *
 * `DateTimePanel` mounts only while the Date/Time sub-panel is open (`TripDetailSubPanel.tsx:52` /
 * `:111`), so nothing resolves the flight for a client who came to change anything else. This hook is
 * what asks — and being the one that asks, it is also the one that answers on save.
 *
 * ## Everything here reads the COMMITTED draft
 *
 * Not one operand comes from panel-local state, because the panel is the thing that is not mounted. The
 * wait, the flight date and the flight number are the booked ones, which is also what makes the answer the
 * same whether or not the client happened to visit the date step.
 *
 * The lookup date is the FLIGHT's day, recovered by subtracting the booked wait from the booked pickup —
 * never the pickup's own day. A 23:45 landing with a 30-minute wait commits a pickup at 00:15 the next
 * day; asking Cirium about THAT day finds no flight, and no landing is sent for precisely the late-arrival
 * bookings the provenance exists for. See {@link flightDateFromPickup}.
 *
 * ## Deduped onto one cache entry
 *
 * The arguments are built the way `DateTimePanel` builds them — same `flightDateFromPickup`, same
 * `extractAirportIata` — so RTK Query serialises them identically and the two subscribers share one entry.
 * Extra subscribers cost extra requests only when they disagree about the argument.
 *
 * ## When it declines to re-derive
 *
 * The same operands the date panel weighs, minus the two that only exist while that panel is open (a
 * touched wait and a touched date — each of those is still that panel's to commit). A CHANGED FLIGHT is
 * NOT among them — see {@link flightTouched}.
 *
 *   - VIEW-ONLY: never fetched, let alone re-derived. A closed booking's pickup is frozen while the flight
 *     keeps moving, so a recomputed one would visibly fail to add up on nearly every booking.
 *   - FROZEN (`at_gate` / `cancelled` / `diverted`): the engine will no longer move this pickup, so
 *     neither may the client — see {@link isFlightPickupFrozen}.
 *   - NOT ARRIVING: cancelled / diverted / unknown flights still carry a scheduled time, but pinning a
 *     pickup to it would be wrong.
 *   - NO STORED ARRIVAL, and NO DRIFT AT ALL: the two terms of the drift question, and both are waived by
 *     {@link flightTouched}. A changed flight needs no comparison — the stored arrival describes the flight
 *     that was REPLACED, so comparing against it answers a question nobody asked. Unchanged, they stand: no
 *     stored arrival means the create path (nothing to compare), and a live landing equal to the stored one
 *     means the flight has not moved.
 *   - NOT AN EDIT: {@link flightTouched} is gated on `editOriginal`, so a create is never moved from here.
 *     The wizard gates Payment behind Trip Details, so every create passes through `DateTimePanel` and that
 *     panel commits the derivation itself.
 *   - AN ELAPSED RE-DERIVATION: drift is the one operand the client cannot move. A flight that landed early
 *     enough puts landing + wait in the past, and re-deriving onto it would hand the client "Pickup time
 *     can't be in the past" on an edit they did not make, with no lever anywhere on the sheet to clear it.
 *     Declining keeps the unrelated edit saveable.
 *
 * ## NO materiality threshold here (DEV-1531)
 *
 * ANY drift re-derives, however small. The 15/30 rule is a churn control for the ENGINE, which wakes
 * repeatedly against a flight that moves all day and would otherwise nudge the same booking a dozen times,
 * each costing a driver re-confirm. An edit is one deliberate human save, so that failure does not exist on
 * this path — and the change-review modal discloses the move before the client confirms, which is the
 * consent step the engine never had. `flightUpdateIsMaterial` therefore stays in `flightPickup.ts` for the
 * engine's rule; only this caller drops it. Supersedes D4/D5 on DEV-1490 for the edit path.
 *
 * ## Committing it is the CALLER's move, on save
 *
 * `applyRederivedPickup` is dispatched from the "Update Booking" click, before the change-review modal
 * renders — see `PricedBookingSummary`. That ordering is load-bearing three ways: the diff dialog gets to
 * disclose `Pickup Time: <old> → <new>` before the client confirms; `pickup_datetime` is in the payload
 * before the server's own `hasPricingRelevantFieldsChanged()` runs, so the booking reprices with no second
 * pricing call; and the CTA's enablement is untouched, so opening a booking and leaving it never looks like
 * an unsaved change.
 *
 * `delayMinutes` normalises a never-asked null to 0, matching `DateTimePanel`'s `delayMin`. Not cosmetic:
 * `buildBookingPayload` drops `flight_landing_used` when the delay is null, so leaving it null would cost
 * the re-derived pickup its provenance and let the server resolve an arrival of its own.
 */
export const useSheetFlightPickup = ({
  syncPickupTerminal = false,
}: {
  /**
   * Drive the arrival-terminal swap from this instance (DEV-1594) — see {@link useTerminalSwap}.
   *
   * Opt-in because the hook is mounted TWICE: once for the sheet's lifetime from `ClientBooking`, and
   * again inside `PricedBookingSummary` for its Update-Booking handler. Everything else here is a memo or
   * a callback and so is free to run in both, but a dispatching effect is not: two instances would each
   * fire the swap off the same resolved flight, and the Google fallback is a billed request. Only the
   * sheet-lifetime mount passes this.
   */
  syncPickupTerminal?: boolean;
} = {}): {
  /** The triple to commit, or null when the pickup must stay exactly as booked. */
  rederived: RederivedFlightPickup | null;
  /** Commit the triple. A no-op when there is nothing to re-derive, so callers need no guard. */
  applyRederivedPickup: () => void;
  /**
   * Put the booked triple back, for a client who saw the disclosure and declined it. A no-op when nothing
   * was applied, so callers need no guard.
   */
  revertRederivedPickup: () => void;
} => {
  const dispatch = useAppDispatch();
  const pickup = useAppSelector(selectPickup);
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const committed = useAppSelector(selectPickupDatetime);
  const committedDelay = useAppSelector(selectPickupFlightDelayMinutes);
  const derivedArrival = useAppSelector(selectPickupFlightDerivedArrival);
  const frozenReason = useAppSelector(selectPickupFlightFrozenReason);
  const committedLandingUsed = useAppSelector(selectPickupFlightLandingUsed);
  const viewOnly = useAppSelector(selectViewOnly);
  const editOriginal = useAppSelector(selectEditOriginal);
  const flightChangedSinceCommit = useAppSelector(selectPickupFlightChangedSinceCommit);

  const delayMin = committedDelay ?? 0;
  const frozen = isFlightPickupFrozen(frozenReason);
  const flightDate = useMemo(
    () => (committed && pickupFlightNumber ? flightDateFromPickup(committed, committedDelay) : null),
    [committed, pickupFlightNumber, committedDelay]
  );

  // Skipped for a frozen booking as well as a closed one: nothing here renders the flight, so a lookup that
  // can never re-derive has no consumer. The date panel still fetches when it is opened, which is what keeps
  // this from changing the request count on any existing flow.
  const { data: flight, currentData: currentFlight, isError } = useFlightDetails({
    flightNumber: pickupFlightNumber ?? "",
    date: flightDate ?? "",
    airport: extractAirportIata(pickup) ?? undefined,
    skip: !pickupFlightNumber || !flightDate || viewOnly || frozen,
  });

  /**
   * The ONE landing every decision here is built from, read through `currentData` rather than `data`.
   *
   * `currentData` is empty exactly while the ARGUMENTS in flight differ from the ones that produced
   * `data` — which is the window a flight change opens. `data` re-uses the last arguments' result, so
   * between "QF994 typed" and "QF994 resolved" it is still QF1424's; a decision taken off it commits the
   * REPLACED flight's landing as `flight_landing_used` under the NEW flight's number, and the
   * change-review modal discloses it as the new flight's time.
   *
   * This is NOT the `isFetching` gate that was deliberately rejected here. That flag is also true while a
   * mount-triggered refetch runs over perfectly good data, and gating on it meant a client who opened the
   * tablet/mobile slide-in summary and tapped Update inside that window saved the booked pickup with
   * nothing disclosed — a silent no-op indistinguishable from "the flight has not moved". `currentData`
   * survives a same-argument refetch and empties only on an argument change, so it separates the two
   * cases the one flag could not. The initial window stays closed by construction: nothing is defined
   * until the first lookup resolves.
   *
   * `DateTimePanel` still needs `isFetching` of its own, because it RENDERS the landing and must not show
   * one mid-refetch.
   */
  const currentLanding = !isError && flightWillArrive(currentFlight) ? landingTime(currentFlight) : null;

  // Live GATE landing against the one the booked pickup was built on — both gate, so they are the same
  // quantity measured at two times. The stored value is UTC while every other flight time here carries the
  // airport's own offset, so it is converted through the arrival airport's timezone rather than read as a
  // wall clock (a Sydney arrival would otherwise render ten hours early).
  const storedLandingIso = useMemo(() => {
    const zone = flight?.arrival?.airport?.timezone;
    return derivedArrival && zone ? utcIsoToAirportIso(derivedArrival, zone) : null;
  }, [derivedArrival, flight?.arrival?.airport?.timezone]);

  /**
   * The client swapped the booking onto a DIFFERENT flight, and this hook is the only thing that will act
   * on it before the save.
   *
   * The flight is changed from the Address step's flight row, which dispatches `setPickupFlightNumber`
   * and nothing else. That reducer sets `pickupFlightChangedSinceCommit`, whose only other reader is
   * `DateTimePanel` — a panel that mounts solely while its sub-panel is open. Without this, a client who
   * changed QF1424 to QF994 and went straight to "Update Booking" saves the PREVIOUS flight's pickup
   * under the new flight's number, with nothing on screen disagreeing.
   *
   * Drift cannot stand in for this. Both of its terms are measured against `pickupFlightDerivedArrival`,
   * which describes the flight that was REPLACED — so a new flight landing at the same minute as the old
   * one reads as "no drift" and holds, which is the wrong answer for the right reason.
   *
   * TWO BASELINES, neither of which covers both cases on its own:
   *
   *   - The slice flag is set by the change itself, so it catches a flight changed on a booking whose
   *     `editOriginal` flight is somehow absent.
   *   - `editOriginal` is the booking as LOADED, and it is what survives a decline. `revertRederivedPickup`
   *     dispatches `setPickupDatetime`, and that reducer clears the flag — so on the flag alone, a client
   *     who read the diff, clicked "Keep Editing" and then clicked "Update Booking" again would silently
   *     save the old flight's pickup. This is the same pair, and the same reasoning, as `DateTimePanel`'s
   *     own `flightTouched`.
   *
   * Gated on a RESOLVED landing FOR THE CURRENT NUMBER — see {@link currentLanding}. The date panel gates
   * its own on `!!landing` and is safe doing so only because it unmounts and remounts around the change;
   * this hook lives across it and would otherwise read the replaced flight's retained entry.
   *
   * Gated on a flight still BEING ON THE BOOKING, which is not the same question as "did it change".
   * `clearPickupFlight` ("Not Flying") and `setPickup` both null the number, and `isSameFlightNumber(null,
   * "QF1424")` is false — so a REMOVAL reads as a change. RTK Query retains `data` across the transition
   * into `skip`, so without this guard the hook re-derives onto the flight just taken off the booking and
   * sends its landing as provenance for a booking that no longer has a flight. `clearPickupFlight`'s
   * contract is the opposite: the client keeps the time they booked. (`useTerminalSwap` guards the same
   * way, and `DateTimePanel` is immune only because `resolvePickup`'s flight branch requires the number.)
   *
   * Compared with `isSameFlightNumber`, the shared rule, rather than raw equality — the modal trims and
   * upper-cases while a rehydrated booking carries the raw payload value, so "qf 994" against a stored
   * "QF994" would read as a change and move a pickup nobody asked to move.
   */
  const flightTouched =
    !!pickupFlightNumber &&
    !!currentLanding &&
    !!editOriginal &&
    (flightChangedSinceCommit || !isSameFlightNumber(pickupFlightNumber, editOriginal.pickupFlightNumber));

  /**
   * The landing this re-derivation is built on, chosen by WHICH question said yes — and read once, so the
   * decision, the committed pickup and the provenance can never come from different arrivals.
   *
   * BOTH questions read {@link currentLanding}, never the retained `data`. Drift keeps its own two terms
   * — a stored arrival, and a live landing that differs from it — but measuring them against a retained
   * result re-opens the window `currentLanding` exists to close from the other side: while the NEW flight
   * resolves, `data` is still the REPLACED flight's, and a booking whose old flight had drifted would
   * offer that old landing as a re-derivation and commit it as `flight_landing_used` under the new
   * number. `flightTouched` cannot catch it — it is false in exactly that window, for want of a resolved
   * current landing — so the drift arm has to decline on its own.
   *
   * This does NOT reintroduce an `isFetching` gate: `currentData` is emptied only when the ARGUMENTS
   * change, and stays populated through a refetch over good data, which is the case the note above the
   * `landing` read exists to protect.
   */
  const drifted = !!currentLanding && !!storedLandingIso && Date.parse(currentLanding) !== Date.parse(storedLandingIso);
  const rederiveLanding = flightTouched || drifted ? currentLanding : null;

  const rederived = useMemo<RederivedFlightPickup | null>(() => {
    if (viewOnly || frozen) return null;
    if (!committed || !rederiveLanding) return null;
    if (validatePickup(pickupFromFlight(rederiveLanding, delayMin))) return null;
    return {
      value: flightPickupCommitValue(rederiveLanding, delayMin).toISOString(),
      delayMinutes: delayMin,
      // Built from the landing THIS lookup resolved, on the day `flightDate` asked about — so the day
      // looked up and the landing sent can never describe different flights.
      landingUsed: flightLandingUsedIso(rederiveLanding),
    };
  }, [viewOnly, frozen, committed, rederiveLanding, delayMin]);

  /**
   * The booked triple as it stood before the re-derivation was committed, held from the moment of apply so
   * a decline can put it back exactly.
   *
   * A ref rather than state: nothing renders from it, and re-rendering the sheet on apply would be a second
   * render for a value only the decline path reads.
   *
   * Only ever written when EMPTY, so it holds what the client actually booked rather than whatever the last
   * apply left behind. Applying twice without a decline in between — the modal is dismissed by a failed save
   * and reopened — must not promote a re-derived pickup into the thing a later decline restores.
   */
  const bookedTriple = useRef<{ value: string | null; delayMinutes: number | null; landingUsed: string | null } | null>(null);

  const applyRederivedPickup = useCallback(() => {
    if (!rederived || !rederiveLanding) return;
    // Re-run the elapsed check against a FRESH now. `rederived` is memoised on flight and draft values, so
    // its own guard was evaluated when the flight last moved — which can be a long edit session before this
    // click. A pickup that was comfortably in the future when the sheet opened can be in the past by the
    // time someone finishes editing their notes, and committing it then hands them "Pickup time can't be in
    // the past" on an edit they did not make: exactly what the guard exists to prevent.
    if (validatePickup(pickupFromFlight(rederiveLanding, delayMin))) return;
    if (!bookedTriple.current) {
      bookedTriple.current = { value: committed, delayMinutes: committedDelay, landingUsed: committedLandingUsed };
    }
    dispatch(setPickupDatetime(rederived.value));
    dispatch(setPickupFlightDelayMinutes(rederived.delayMinutes));
    dispatch(setPickupFlightLandingUsed(rederived.landingUsed));
  }, [dispatch, rederived, rederiveLanding, delayMin, committed, committedDelay, committedLandingUsed]);

  /**
   * The pickup is committed BEFORE the client agrees to it, because that is what puts it in the diff they
   * read — the disclosure has to precede the confirmation. Declining therefore has to undo it, or the sheet
   * keeps a change the client just refused: the CTA stays enabled on a pickup nobody moved, and the price
   * summary stays flipped from the invoiced stored price to a live re-quote.
   *
   * All three go back together, for the same reason they are committed together. Restoring the pickup while
   * leaving the landing behind would hand the server `pickup != arrival + wait` on the next save.
   */
  const revertRederivedPickup = useCallback(() => {
    const booked = bookedTriple.current;
    if (!booked) return;
    bookedTriple.current = null;
    dispatch(setPickupDatetime(booked.value));
    dispatch(setPickupFlightDelayMinutes(booked.delayMinutes));
    dispatch(setPickupFlightLandingUsed(booked.landingUsed));
  }, [dispatch]);

  const arrivalIso = landingTime(flight);
  useTerminalSwap({
    // `isError` for the same reason `landing` above weighs it: RTK Query retains the last good `data`
    // through a failed refetch, and rewriting the pickup off an arrival the server has since stopped
    // confirming is worse than leaving it. `flightWillArrive` for the reason the re-derivation weighs
    // it — a cancelled flight still carries a terminal, and moving the pickup off a flight that is not
    // coming is worse than leaving it.
    enabled: syncPickupTerminal && !viewOnly && !frozen && !isError,
    legResolved: flightWillArrive(flight) && !!arrivalIso,
    flightNumber: pickupFlightNumber,
    location: pickup,
    leg: {
      iata: flight?.arrival?.airport?.iata ?? null,
      name: flight?.arrival?.airport?.name ?? null,
      terminal: flight?.arrival?.terminal ?? null,
    },
    onSwap: (poi) =>
      dispatch(
        swapPickupForTerminal({
          pickup: poi,
          // Non-null by `legResolved` above — the swap never runs without a resolved arrival.
          source: { flightNumber: pickupFlightNumber ?? "", terminal: flight?.arrival?.terminal ?? "", arrivalIso: arrivalIso ?? "" },
        })
      ),
  });

  return { rederived, applyRederivedPickup, revertRederivedPickup };
};
